import { Grid, Typography } from "@mui/material";
import { DateField } from "components/Forms/DateField";
import { Issue, WasteEpisodicEventType } from "generated-graphql/graphql";
import { Control, useWatch } from "react-hook-form";

import { EpisodicEventFormValues } from "./episodicEventFormTypes";
import { NoticeProofDocument, NoticeProofFormField } from "./noticeProof";

/** The `episodicPlannedNoticeViolation` key surfaced by
 * `episodicEventInputValidator` — see `episodicEventRules.ts`. */
const PLANNED_NOTICE_ISSUE_KEY = "episodicPlannedNotice";

/**
 * Optional external-filing capture for a planned event that can't meet the
 * 30-day EPA notice window (flagged by the `episodicPlannedNotice` validator
 * issue). There's no yes/no question here — filling in the date is the
 * answer. Self-gates on `type` via `useWatch`, matching `EpaContactSection`.
 *
 * No inline late-filing alert here: the validator's `episodicPlannedNotice`
 * warning (rendered above the dates via `IssueAlerts`) already says the event
 * starts inside the 30-day window, and this section's own caption says why
 * it's asking — a second alert repeating the same lateness verdict once a
 * date is entered was pure noise. `RecordNoticeDialog`'s card-side warning is
 * unaffected; it has no such duplicate to remove.
 */
export function ExternalFilingSection({
  control,
  issues,
  existingProof = null,
}: {
  control: Control<EpisodicEventFormValues>;
  issues: Issue[];
  /** Proof already attached to the recorded FORM_FILED_EXTERNALLY notice, if
   * any — shown as kept, so a date correction doesn't demand a re-upload. */
  existingProof?: NoticeProofDocument | null;
}) {
  const type = useWatch({ control, name: "type" });
  const filingDate = useWatch({ control, name: "externalFilingDate" });
  const hasPlannedNoticeIssue = issues.some((issue) => issue.key === PLANNED_NOTICE_ISSUE_KEY);

  // Renders for the planned-notice issue (the capture prompt) OR whenever a
  // filing date already exists — a recorded filing must keep its section and
  // proof field on screen even after the start date moves past the 30-day
  // window, or a save-time error on the proof field would land unmounted and
  // read as a silent dead end.
  if (type !== WasteEpisodicEventType.Planned || (!hasPlannedNoticeIssue && filingDate == null)) {
    return null;
  }

  return (
    <>
      <Grid size={{ xs: 12 }}>
        <Typography variant="caption" color="text.secondary">
          Was the 8700-12 already filed outside Encamp?
        </Typography>
      </Grid>
      <Grid size={{ xs: 6 }}>
        <DateField name="externalFilingDate" label="Filed on" control={control} />
      </Grid>
      {filingDate != null && (
        <Grid size={{ xs: 12 }}>
          <NoticeProofFormField
            control={control}
            name="externalFilingProofFile"
            existingProof={existingProof}
            helperText="Upload a copy of the filed Form 8700-12 (required)."
          />
        </Grid>
      )}
    </>
  );
}
